import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { Collapse } from '@fortune-ui'
import { Box } from '@pets-ui/Box/Box'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { MemberCouponBadgeCount } from '@wp-site-wide/components/HeaderMemberCouponBadge/MemberCouponBadgeCount'
import { type ReactNode, useState } from 'react'
import { useLocation } from 'react-router-dom-v5'
import { isMatchPath } from '../utils/isMatchPath'
import { SidebarItem } from './SidebarItem'
import { SidebarParentItem } from './SidebarParentItem'

const styles = stylex.create({
  sidebarContainer: {
    userSelect: 'none',
  },
  sidebarLink: (active: boolean) => ({
    transition: 'color 0.2s',
    color: {
      'default': colors[`${active ? 'grey900' : 'grey600'}`],
      ':hover': colors.grey900,
    },
  }),
  link: {
    textDecoration: 'none',
    cursor: 'pointer',
  },
})

export interface MenuItem {
  title: string
  key: string
  activeKeys?: string[]
  children?: MenuItem[]
  show: boolean
  disabled?: boolean
  onClick?: () => void
  render?: (isSelected?: boolean) => ReactNode
  isNotifying?: boolean
  badgeCount?: number
  testId?: string
  badgeTestId?: string
}

export interface WpMyAccountSidebarProps {
  menuItem: MenuItem[]
  /** navigation to route */
  onNavigation?: (route: string) => void
}

export function WpMyAccountSidebar({ menuItem, onNavigation }: WpMyAccountSidebarProps) {
  const [openMenuSet, setOpenMenuSet] = useState(new Set())
  const { pathname } = useLocation()
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  const checkActive = (item: MenuItem) => {
    if (isMatchPath(item.key, pathname)) return true
    return item.activeKeys?.some(activeKey => isMatchPath(activeKey, pathname)) ?? false
  }

  // NOTE: save match key once from pathname
  useEffectOnce(() => {
    const isMatchPathParentKey = menuItem.find(item => item.children?.some(child => checkActive(child)))?.key
    setOpenMenuSet(isMatchPathParentKey ? new Set([isMatchPathParentKey]) : new Set())
  })

  const handleClickParent = (key: string) => {
    const isOpenMenu = openMenuSet.has(key)
    setOpenMenuSet(isOpenMenu ? new Set() : new Set([key]))
  }

  const handleClickChild = (key: string, onClick?: () => void) => () => {
    onClick?.()
    onNavigation?.(key)
    sendEvent({
      eventName: 'click_my_account_menu_item',
      eventParams: {
        event_category: 'menu item',
        page_key: key,
      },
    })
  }

  return (
    <Box borderRadius={8} backgroundColor={colors.white}>
      {menuItem.filter(item => item.show).map((item) => {
        if (item.render) return item.render(checkActive(item))

        const atChildPath = !!item.children?.some(child => checkActive(child))
        const isOpenMenu = openMenuSet.has(item.key)
        return (
          <Box key={item.key} rootStyle={styles.sidebarContainer}>
            {item.children
              ? (
                <>
                  <SidebarParentItem
                    isOpen={isOpenMenu}
                    active={atChildPath}
                    value={item.title}
                    onClick={() => handleClickParent(item.key)}
                    isNotifying={item.isNotifying}
                  />
                  <Collapse in={isOpenMenu}>
                    <Box py={3} backgroundColor={colors.grey50}>
                      {item.children.filter(child => child.show).map((child) => {
                        if (child.render) {
                          return child.render(checkActive(child))
                        }
                        return (
                          <SidebarItem
                            key={child.key}
                            to={child.onClick ? '' : child.key}
                            active={checkActive(child)}
                            value={child.title}
                            onClick={handleClickChild(child.key, child.onClick)}
                          />
                        )
                      })}
                    </Box>
                  </Collapse>

                </>
              )
              : item.disabled
                ? (
                  <Box p={4} onClick={handleClickChild(item.key, item.onClick)}>
                    <Text variant="body">{item.title}</Text>
                  </Box>
                )
                : (
                  <a
                    data-testid={item.testId}
                    {...stylex.props(styles.link)}
                    href={(item.onClick) ? undefined : item.key}
                  >
                    <Box p={4} display="flex" alignItems="center" justifyContent="flex-start" gap={1} onClick={handleClickChild(item.key, item.onClick)}>
                      <Text
                        variant="subtitle"
                        bold={checkActive(item)}
                        rootStyle={[styles.sidebarLink(checkActive(item))]}
                      >
                        {item.title}
                      </Text>
                      {!!item.badgeCount && (
                        <MemberCouponBadgeCount couponCount={item.badgeCount} menuBadgeBgColor={colors.red600} testId={item.badgeTestId} />
                      )}
                    </Box>
                  </a>
                )}
          </Box>
        )
      })}
    </Box>
  )
}
